import type { VideoCallScreenShareDeniedReason } from "~/types/videoCalls";
import { getApiErrorCode, getApiErrorMessage } from "~/utils/apiError";

export const VIDEO_CALL_INVITATION_UNAVAILABLE_MESSAGE =
  "O convite expirou, foi substituído ou a chamada já terminou. Peça ao professor um novo link para entrar.";

const SCREEN_SHARE_DENIED_MESSAGES: Record<
  VideoCallScreenShareDeniedReason,
  string
> = {
  // A deliberate cancellation in the browser picker deserves no error at all.
  dismissed: "",
  presenter_busy:
    "Outra pessoa está compartilhando a tela agora. Peça para ela parar antes de começar.",
  not_allowed: "Você não tem permissão para compartilhar a tela nesta chamada.",
  unsupported:
    "Este navegador não permite compartilhar a tela. Tente pelo Chrome, Edge ou Firefox no computador.",
  failed:
    "Não foi possível compartilhar a tela. Tente novamente em alguns instantes.",
};

export const getScreenShareDeniedMessage = (
  reason: VideoCallScreenShareDeniedReason,
): string => SCREEN_SHARE_DENIED_MESSAGES[reason] ?? "";

export const getScreenSharePresenterBusyMessage = (
  presenterName: string | null,
): string =>
  presenterName
    ? `${presenterName} está compartilhando a tela agora. Peça para essa pessoa parar antes de começar.`
    : SCREEN_SHARE_DENIED_MESSAGES.presenter_busy;

const getResponseStatus = (error: unknown): number | null => {
  if (typeof error !== "object" || error === null) {
    return null;
  }

  const response = (error as { response?: unknown }).response;
  if (typeof response !== "object" || response === null) {
    return null;
  }

  const status = (response as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
};

const isGenericNotFoundMessage = (message: string): boolean => {
  const normalizedMessage = message
    .trim()
    .replace(/[.!]+$/u, "")
    .toLocaleLowerCase("pt-BR");

  return ["not found", "não encontrado", "não encontrada"].includes(
    normalizedMessage,
  );
};

export const replaceGenericNotFoundMessage = (
  message: string | null | undefined,
  fallbackMessage: string,
): string => {
  const normalizedMessage = message?.trim();
  if (!normalizedMessage || isGenericNotFoundMessage(normalizedMessage)) {
    return fallbackMessage;
  }

  return normalizedMessage;
};

export const isVideoCallInvitationUnavailableError = (
  error: unknown,
): boolean => {
  const status = getResponseStatus(error);
  if (status === 404 || status === 410) {
    return true;
  }

  if (getApiErrorCode(error) === "video_call_unavailable") {
    return true;
  }

  return isGenericNotFoundMessage(getApiErrorMessage(error, ""));
};

export const getVideoCallErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string =>
  replaceGenericNotFoundMessage(
    getApiErrorMessage(error, fallbackMessage),
    fallbackMessage,
  );
